import {useIntersectionObserver, useIsClient, useResizeObserver} from 'usehooks-ts'
import {useSuspenseQuery} from '@tanstack/react-query'
import {HiOutlineRocketLaunch} from 'react-icons/hi2'
import {useNavigate} from '@tanstack/react-router'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslations} from 'use-intl'
import Confetti from 'react-confetti'
import useBasketOverlayState from '~/hooks/useBasketOverlayState'
import {promotionsQueryOptions} from '~/client/fn/promotions'
import {usePromotionMutation} from '~/hooks/useBasket'
import useLocaleParams from '~/hooks/useLocaleParams'
import {useIsMinMedium} from '~/hooks/useScreen'
import {PromotionType} from '~/gql/graphql'

const PromotionBanner = () => {
    const params = useLocaleParams()
    const navigate = useNavigate()
    const ref = useRef<HTMLDivElement>(null)
    const isClient = useIsClient()
    const isDesktop = useIsMinMedium()
    const t = useTranslations('promotion')
    const {data: promotions} = useSuspenseQuery(promotionsQueryOptions())
    const promotion = useMemo(() => promotions[0], [promotions])
    const {showBasketOverlay} = useBasketOverlayState()

    const onApplySuccess = useCallback(() => {
        if (isDesktop) {
            showBasketOverlay()
        } else {
            void navigate({
                to: '/$lang/basket',
                params: params,
                resetScroll: true,
            })
        }
    }, [isDesktop, showBasketOverlay, navigate, params])

    const {mutate: applyPromotionCode, isSuccess} = usePromotionMutation({
        onSuccess: onApplySuccess,
    })

    const {isIntersecting, ref: bannerRef} = useIntersectionObserver({
        threshold: 0.1,
        initialIsIntersecting: true,
    })

    const {width = 0, height = 0} = useResizeObserver({ref, box: 'border-box'})

    const [confettiRunning, setConfettiRunning] = useState(true)
    useEffect(() => {
        const timer = setTimeout(() => setConfettiRunning(false), 8000)
        return () => clearTimeout(timer)
    }, [])

    const message = useMemo(() => {
        if (!promotion) {
            return null
        }

        if (!!promotion.discountPercent && promotion.discountPercent > 0) {
            if (!!promotion.minBasketItems && promotion.minBasketItems > 0) {
                return t('discountMinBasketItems', {
                    percent: promotion.discountPercent,
                    min: promotion.minBasketItems,
                })
            }

            return t('discount', {percent: promotion.discountPercent})
        }

        if (!!promotion.freeShipping) {
            if (!!promotion.minBasketItems && promotion.minBasketItems > 0) {
                return t('freeShippingMinBasketItems', {
                    min: promotion.minBasketItems,
                })
            }

            return t('freeShipping')
        }

        return null
    }, [t, promotion])

    if (!promotion || isSuccess) {
        return null
    }

    return (
        <div
            ref={bannerRef}
            className="bg-gradient-to-r from-nova/80 via-nebula/80 to-nova/80 w-full z-10 relative"
        >
            <h2 className="sr-only">{t('title')}</h2>
            {isClient && (
                <Confetti
                    className="mx-auto motion-reduce:hidden"
                    width={width}
                    height={height}
                    numberOfPieces={isDesktop ? 50 : 20}
                    initialVelocityX={2}
                    initialVelocityY={4}
                    gravity={0.025}
                    friction={0.95}
                    wind={0}
                    tweenDuration={1000}
                    run={
                        confettiRunning &&
                        isIntersecting &&
                        (promotion.type === PromotionType.Special ||
                            promotion.type === PromotionType.Welcome)
                    }
                    recycle={confettiRunning}
                />
            )}
            <div
                ref={ref}
                className="mx-auto max-w-screen-xl text-sm md:text-base flex flex-row flex-wrap items-center justify-between md:justify-center gap-2 gap-x-4 md:gap-x-6 py-3 px-4 sm:px-6"
            >
                <p className="font-bold flex flex-row items-center gap-2">
                    <HiOutlineRocketLaunch className="size-4 md:size-5" />
                    <span>{message}</span>
                </p>
                <button
                    className="text-xs md:text-sm border border-white/40 rounded py-1.5 px-3 font-medium bg-white/10 hover:bg-white/20 transition-colors"
                    onClick={() => applyPromotionCode(promotion.code ?? undefined)}
                >
                    {t('apply')}
                </button>
            </div>
        </div>
    )
}

export default PromotionBanner
